import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import {
    NotificationMethod,
    ReceivingFacility,
    ReceivingFacilityContact,
} from "../../interfaces/ReceivingFacility";
import ReceivingFacilityService from "../../services/ReceivingFacilityService";
import { FacilityMap } from "./FacilityMap";

const NOTIFICATION_METHODS: NotificationMethod[] = ["sms", "sentio_web"];
// International format required by the SMS provider, e.g. +4712345678.
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

interface Location {
    display_address: string;
    latitude: number;
    longitude: number;
    address_verified: boolean;
}

interface Props {
    facility?: ReceivingFacility;
    open: boolean;
    readOnly: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const emptyContact = (): ReceivingFacilityContact => ({
    name: "",
    phone: "",
    email: "",
    role: "",
    department: "",
    priority: 0,
});

const formatApiError = (error: any, fallback: string): string => {
    const data = error?.data;
    if (!data || typeof data !== "object") return fallback;
    if (typeof data.detail === "string") return data.detail;
    const flatten = (value: any): string[] => {
        if (Array.isArray(value)) return value.flatMap(flatten);
        if (value && typeof value === "object") {
            return Object.entries(value).flatMap(([key, v]) =>
                flatten(v).map((msg) => (key === "non_field_errors" ? msg : `${key}: ${msg}`))
            );
        }
        return value ? [String(value)] : [];
    };
    const messages = flatten(data);
    return messages.length ? messages.join("\n") : fallback;
};

export const ReceivingFacilityDialog = ({ facility, open, readOnly, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [cookies] = useCookies(["access_token"]);
    const isNew = !facility?.id;

    const [name, setName] = useState(facility?.name ?? "");
    const [address, setAddress] = useState(facility?.address ?? "");
    const [location, setLocation] = useState<Location | null>(
        facility
            ? {
                  display_address: facility.display_address,
                  latitude: Number(facility.latitude),
                  longitude: Number(facility.longitude),
                  address_verified: facility.address_verified,
              }
            : null
    );
    const [notificationMethods, setNotificationMethods] = useState<NotificationMethod[]>(
        facility?.notification_methods ?? ["sentio_web"]
    );
    const [contacts, setContacts] = useState<ReceivingFacilityContact[]>(
        facility?.contacts?.length
            ? [...facility.contacts].sort((a, b) => a.priority - b.priority)
            : [emptyContact()]
    );

    const [lookingUp, setLookingUp] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState("");

    const lookupAddress = () => {
        setLookingUp(true);
        setError("");
        ReceivingFacilityService.validateAddress(address, cookies.access_token)
            .then((result) => {
                setAddress(result.address);
                setLocation({
                    display_address: result.display_address,
                    latitude: result.latitude,
                    longitude: result.longitude,
                    address_verified: result.address_verified,
                });
            })
            .catch((err) => {
                setLocation(null);
                setError(formatApiError(err, t("rf_address_not_found")));
            })
            .finally(() => setLookingUp(false));
    };

    const toggleMethod = (method: NotificationMethod) => {
        setNotificationMethods((current) =>
            current.includes(method) ? current.filter((m) => m !== method) : [...current, method]
        );
    };

    const updateContact = (index: number, field: keyof ReceivingFacilityContact, value: string) => {
        setContacts((current) =>
            current.map((c, i) => (i === index ? { ...c, [field]: value } : c))
        );
    };

    const moveContactUp = (index: number) => {
        setContacts((current) => {
            const next = [...current];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next;
        });
    };

    const removeContact = (index: number) => {
        setContacts((current) => current.filter((_, i) => i !== index));
    };

    const contactsValid =
        contacts.length > 0 &&
        contacts.every((c) => c.name.trim() && PHONE_PATTERN.test(c.phone) && c.email.trim());
    const canSave =
        !readOnly &&
        !saving &&
        name.trim() !== "" &&
        location !== null &&
        notificationMethods.length > 0 &&
        contactsValid;

    const handleSave = () => {
        if (!location) return;
        setSaving(true);
        setError("");
        const payload: ReceivingFacility = {
            name: name.trim(),
            address: address.trim(),
            display_address: location.display_address,
            latitude: Number(location.latitude.toFixed(6)),
            longitude: Number(location.longitude.toFixed(6)),
            address_verified: location.address_verified,
            notification_methods: notificationMethods,
            contacts: contacts.map(({ id, ...c }, i) => ({ ...c, priority: i + 1 })),
        };
        const request = isNew
            ? ReceivingFacilityService.create(payload, cookies.access_token)
            : ReceivingFacilityService.update(facility!.id!, payload, cookies.access_token);
        request
            .then(() => onSaved())
            .catch((err) => setError(formatApiError(err, t("rf_save_failed"))))
            .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        if (!facility?.id) return;
        setSaving(true);
        setError("");
        ReceivingFacilityService.remove(facility.id, cookies.access_token)
            .then(() => onSaved())
            .catch((err) => {
                setError(formatApiError(err, t("rf_delete_failed")));
                setConfirmDelete(false);
            })
            .finally(() => setSaving(false));
    };

    const title = readOnly
        ? t("rf_view_facility")
        : isNew
        ? t("rf_add_facility")
        : t("rf_edit_facility");

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label={t("rf_name")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            InputProps={{ readOnly }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label={t("rf_address")}
                            value={address}
                            onChange={(e) => {
                                setAddress(e.target.value);
                                // Coordinates must always be derived from the saved address
                                setLocation(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !readOnly && address.trim().length >= 3) {
                                    e.preventDefault();
                                    lookupAddress();
                                }
                            }}
                            helperText={!readOnly && !location ? t("rf_address_helper") : undefined}
                            InputProps={{
                                readOnly,
                                endAdornment: !readOnly && (
                                    <InputAdornment position="end">
                                        {lookingUp ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <Button
                                                startIcon={<SearchIcon />}
                                                onClick={lookupAddress}
                                                disabled={address.trim().length < 3}
                                            >
                                                {t("rf_find_address")}
                                            </Button>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {location && (
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                {location.address_verified && (
                                    <Tooltip title={t("rf_address_verified")}>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    </Tooltip>
                                )}
                                <Typography variant="body2">{location.display_address}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <FacilityMap
                                    latitude={location.latitude}
                                    longitude={location.longitude}
                                />
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormLabel component="legend">{t("rf_notification_methods")}</FormLabel>
                        <FormGroup row>
                            {NOTIFICATION_METHODS.map((method) => (
                                <FormControlLabel
                                    key={method}
                                    control={
                                        <Checkbox
                                            checked={notificationMethods.includes(method)}
                                            onChange={() => toggleMethod(method)}
                                            disabled={readOnly}
                                        />
                                    }
                                    label={t(`rf_method_${method}`)}
                                />
                            ))}
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <FormLabel component="legend">{t("rf_contacts")}</FormLabel>
                        <Typography variant="caption" color="text.secondary">
                            {t("rf_contacts_helper")}
                        </Typography>
                    </Grid>

                    {contacts.map((contact, index) => (
                        <Grid item xs={12} key={contact.id ?? `new-${index}`}>
                            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                        {t("rf_priority")} {index + 1}
                                    </Typography>
                                    {!readOnly && (
                                        <>
                                            <IconButton
                                                size="small"
                                                disabled={index === 0}
                                                onClick={() => moveContactUp(index)}
                                                aria-label={t("rf_move_up")}
                                            >
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={contacts.length === 1}
                                                onClick={() => removeContact(index)}
                                                aria-label={t("delete")}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                                <Grid container spacing={2}>
                                    {(
                                        [
                                            ["name", true],
                                            ["phone", true],
                                            ["email", true],
                                            ["role", false],
                                            ["department", false],
                                        ] as [keyof ReceivingFacilityContact, boolean][]
                                    ).map(([field, required]) => {
                                        const phoneInvalid =
                                            field === "phone" && !readOnly && contact.phone !== "" && !PHONE_PATTERN.test(contact.phone);
                                        return (
                                        <Grid item xs={12} sm={field === "department" ? 12 : 6} key={field}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                required={required}
                                                type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                                                label={t(`rf_contact_${field}`)}
                                                value={contact[field] ?? ""}
                                                onChange={(e) => updateContact(index, field, e.target.value)}
                                                error={phoneInvalid}
                                                helperText={field === "phone" && !readOnly ? t("rf_phone_format") : undefined}
                                                placeholder={field === "phone" ? "+4712345678" : undefined}
                                                InputProps={{ readOnly }}
                                            />
                                        </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        </Grid>
                    ))}

                    {!readOnly && (
                        <Grid item xs={12}>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={() => setContacts((c) => [...c, emptyContact()])}
                            >
                                {t("rf_add_contact")}
                            </Button>
                        </Grid>
                    )}

                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
                                {error}
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                {!readOnly && !isNew && (
                    <Button
                        color="error"
                        variant={confirmDelete ? "contained" : "text"}
                        disabled={saving}
                        onClick={confirmDelete ? handleDelete : () => setConfirmDelete(true)}
                        sx={{ mr: "auto" }}
                    >
                        {confirmDelete ? t("confirm_delete") : t("delete")}
                    </Button>
                )}
                <Button onClick={onClose}>{readOnly ? t("close") : t("cancel")}</Button>
                {!readOnly && (
                    <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                        {t("save")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
